// ---------------------------------------------------------------------------
// 損益分岐点 部門カード grid — 各部門(全社/店舗合計/グループ/店舗/非店舗)を1枚の損益カードに。
//   カード頭=部門名+営業利益(大)+黒字/赤字、売上 vs 損益分岐点バー、主要指標、▾で全指標。
//   数値はフル桁(¥0,000,000)、% は有効数字2桁。市場家賃版は未投入時「未確定」。
//   セクション(サマリ/各飲食グループ/その他部門)ごとに色アクセントで束ねる。
// ---------------------------------------------------------------------------
import React, { useState } from 'react';
import { MdExpandMore, MdExpandLess } from 'react-icons/md';
import { BreakevenMetrics } from '../api/client';
import { MatrixColumn } from './BreakevenMatrix';

const INK = '#2a2723';
const SUB = '#8c877f';
const FAINT = '#b3aea6';
const GREEN = '#1f8a4c';
const RED = '#d83a36';
const ORANGE = '#d98324';
const PURPLE = '#7c3aed';
const LINE = '#eeece8';

// --- 書式 ---
const yen = (v: number | null | undefined): string =>
  v == null ? '–' : `${v < 0 ? '▲' : ''}¥${Math.abs(Math.round(v)).toLocaleString('ja-JP')}`;
// 小数点以下2桁の%
const sig2 = (v: number | null | undefined): string => {
  if (v == null || !isFinite(v)) return '–';
  return `${(v * 100).toFixed(2)}%`;
};
const hours = (v: number | null | undefined): string => (v == null || v === 0 ? '–' : `${Math.round(v).toLocaleString('ja-JP')}h`);
const yenh = (v: number | null | undefined): string => (v == null || v === 0 ? '–' : `¥${Math.round(v).toLocaleString('ja-JP')}`);

type Fmt = 'yen' | 'pct' | 'hours' | 'yenh' | 'yoy';
interface RowD { label: string; get?: (m: BreakevenMetrics) => number | null | undefined; fmt: Fmt; market?: boolean; }

const KEY_ROWS: RowD[] = [
  { label: '売上', get: (m) => m.売上, fmt: 'yen' },
  { label: '粗利益', get: (m) => m.粗利益, fmt: 'yen' },
  { label: '損益分岐点売上', get: (m) => m.損益分岐点, fmt: 'yen' },
  { label: '安全余裕率', get: (m) => m.安全余裕率, fmt: 'pct' },
  { label: '市場損益', get: (m) => m.営業利益_市場, fmt: 'yen', market: true },
];
const MORE: { sec: string; rows: RowD[] }[] = [
  { sec: '売上・原価', rows: [
    { label: '前年比', fmt: 'yoy' },
    { label: '原材料', get: (m) => m.原材料, fmt: 'yen' },
    { label: '原価率', get: (m) => m.原価率, fmt: 'pct' },
  ] },
  { sec: '人件費', rows: [
    { label: '労務費', get: (m) => m.労務費, fmt: 'yen' },
    { label: '労務比率', get: (m) => (m.売上 ? m.労務費 / m.売上 : null), fmt: 'pct' },
  ] },
  { sec: '販売費・一般管理費', rows: [
    { label: '販売費', get: (m) => m.販売費, fmt: 'yen' },
    { label: '販売費（市場家賃）', get: (m) => m.販売費 + m.家賃減額差, fmt: 'yen', market: true },
    { label: '一般管理費', get: (m) => m.一般管理費, fmt: 'yen' },
    { label: '一般管理費（市場家賃）', get: (m) => m.一般管理費_市場, fmt: 'yen', market: true },
  ] },
  { sec: '損益分岐点', rows: [
    { label: '損益分岐点（市場家賃）', get: (m) => m.損益分岐点_市場, fmt: 'yen', market: true },
    { label: '達成率', get: (m) => m.達成率, fmt: 'pct' },
    { label: '家賃減額差', get: (m) => m.家賃減額差, fmt: 'yen' },
  ] },
  { sec: '生産性', rows: [
    { label: '勤務時間', get: (m) => m.勤務時間, fmt: 'hours' },
    { label: '人時生産性', get: (m) => m.人時生産性, fmt: 'yenh' },
  ] },
  { sec: '営業外・経常・CF', rows: [
    { label: '減価償却費', get: (m) => m.減価償却費, fmt: 'yen' },
    { label: '営業外利益', get: (m) => m.営業外収益, fmt: 'yen' },
    { label: '営業外費用', get: (m) => m.営業外費用, fmt: 'yen' },
    { label: '経常利益', get: (m) => m.経常利益, fmt: 'yen' },
    { label: '銀行返済', get: (m) => m.銀行返済, fmt: 'yen' },
    { label: 'CF', get: (m) => m.CF, fmt: 'yen' },
    { label: 'CF必要売上', get: (m) => m.CF必要売上, fmt: 'yen' },
  ] },
];

const CAT: Record<string, string> = {
  summary: '#3b6fd4', soba: '#0d9488', restaurant: '#c2761f', cafe: '#2f9e54', washoku: '#6366f1', nonstore: '#7c756c',
};

const num: React.CSSProperties = { fontVariantNumeric: 'tabular-nums', fontFeatureSettings: '"tnum"' };

function rowText(r: RowD, col: MatrixColumn, src: 'metrics' | 'prior'): string {
  const m = src === 'metrics' ? col.metrics : (col.prior ?? null);
  if (!m) return '–';
  if (r.market && col.marketMissing) return '未確定';
  if (r.fmt === 'yoy') {
    const c = col.metrics?.売上, p = col.prior?.売上;
    if (src === 'prior' || c == null || !p) return '';
    return sig2(c / p);
  }
  const v = r.get ? r.get(m) : null;
  if (v == null) return '–';
  if (r.fmt === 'pct') return sig2(v);
  if (r.fmt === 'hours') return hours(v);
  if (r.fmt === 'yenh') return yenh(v);
  return yen(v);
}

const MetricLine: React.FC<{ r: RowD; col: MatrixColumn; showPrev: boolean; strong?: boolean }> = ({ r, col, showPrev, strong }) => {
  const missing = r.market && col.marketMissing;
  const prev = showPrev ? rowText(r, col, 'prior') : '';
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', padding: '4px 0', borderBottom: `1px solid ${LINE}`, gap: 10 }}>
      <span style={{ fontSize: '0.75rem', color: r.market ? PURPLE : SUB, fontWeight: r.market ? 600 : 500, whiteSpace: 'nowrap' }}>{r.label}</span>
      <span style={{ textAlign: 'right', ...num }}>
        <span style={{ fontSize: strong ? '0.9rem' : '0.82rem', fontWeight: strong ? 800 : 700, color: missing ? FAINT : (r.market ? PURPLE : INK) }}>
          {rowText(r, col, 'metrics')}
        </span>
        {showPrev && prev && <span style={{ display: 'block', fontSize: '0.625rem', color: FAINT, marginTop: 1 }}>前年 {prev}</span>}
      </span>
    </div>
  );
};

const DeptCard: React.FC<{ col: MatrixColumn; accent: string; featured: boolean; showPrev: boolean }> = ({ col, accent, featured, showPrev }) => {
  const [open, setOpen] = useState(false);
  const m = col.metrics;
  if (!m) {
    return (
      <div style={{ border: `1px solid ${LINE}`, borderRadius: 14, padding: 16, background: '#fff' }}>
        <div style={{ fontWeight: 700, color: INK }}>{col.label}</div>
        <div style={{ color: FAINT, fontSize: '0.8rem', marginTop: 8 }}>データなし</div>
      </div>
    );
  }
  const op = m.営業利益;
  const positive = op >= 0;
  const sales = m.売上 || 0;
  const bep = m.損益分岐点 || 0;
  const ach = m.達成率;
  const scaleMax = Math.max(sales, bep) || 1;
  const salesPct = Math.max(0, Math.min(100, (sales / scaleMax) * 100));
  const bepPct = Math.max(0, Math.min(100, (bep / scaleMax) * 100));
  const over = ach != null && ach >= 1;
  const barColor = over ? GREEN : ach != null && ach >= 0.95 ? ORANGE : RED;
  const opPrev = showPrev && col.prior ? col.prior.営業利益 : null;

  return (
    <div style={{
      border: `1px solid ${featured ? accent + '44' : LINE}`, borderRadius: 14, background: '#fff',
      boxShadow: featured ? `0 1px 3px ${accent}1a` : '0 1px 2px rgba(40,35,30,0.04)',
      overflow: 'hidden', display: 'flex', flexDirection: 'column',
    }}>
      {/* 頭: 部門名 + 営業利益 */}
      <div style={{ padding: '13px 15px 11px', borderTop: `3px solid ${accent}`, background: featured ? `linear-gradient(180deg, ${accent}0c, #fff)` : '#fff' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontSize: featured ? '0.95rem' : '0.875rem', fontWeight: 800, color: INK, letterSpacing: '0.01em' }}>{col.label}</span>
          <span style={{ fontSize: '0.6rem', fontWeight: 700, color: '#fff', background: accent, borderRadius: 5, padding: '2px 7px' }}>
            {col.kind === 'total' ? 'サマリ' : col.kind === 'group' ? 'グループ' : col.kind === 'nonstore' ? '部門' : '店舗'}
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginTop: 6 }}>
          <span style={{ fontSize: '0.7rem', color: SUB, fontWeight: 700 }}>営業利益</span>
          <span style={{ fontSize: '1.45rem', fontWeight: 800, color: positive ? GREEN : RED, ...num, lineHeight: 1.1 }}>{yen(op)}</span>
          <span style={{ fontSize: '0.62rem', fontWeight: 800, color: positive ? GREEN : RED, background: positive ? '#e7f4ec' : '#fce9e8', borderRadius: 5, padding: '1px 6px' }}>
            {positive ? '黒字' : '赤字'}
          </span>
        </div>
        {showPrev && opPrev != null && (
          <div style={{ fontSize: '0.625rem', color: FAINT, marginTop: 2, ...num }}>前年 {yen(opPrev)}</div>
        )}
      </div>

      {/* 売上 vs 損益分岐点 バー */}
      <div style={{ padding: '0 15px 11px' }}>
        <div style={{ position: 'relative', height: 9, borderRadius: 5, background: '#f0eeea', overflow: 'visible' }}>
          <div style={{ position: 'absolute', left: 0, top: 0, height: '100%', width: `${salesPct}%`, background: barColor, borderRadius: 5, transition: 'width .3s' }} />
          <div style={{ position: 'absolute', left: `${bepPct}%`, top: -2, height: 13, width: 2, background: '#6b7280', borderRadius: 1 }} title="損益分岐点" />
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 5, fontSize: '0.66rem', color: SUB, ...num }}>
          <span>達成率 <b style={{ color: barColor }}>{sig2(ach)}</b></span>
          <span style={{ color: over ? GREEN : RED }}>{over ? `余裕 ${yen(sales - bep)}` : `黒字まで ${yen(bep - sales)}`}</span>
        </div>
      </div>

      {/* 主要指標 */}
      <div style={{ padding: '2px 15px 12px' }}>
        {KEY_ROWS.map((r) => <MetricLine key={r.label} r={r} col={col} showPrev={showPrev} strong />)}
      </div>

      {/* 全指標 (展開) */}
      {open && (
        <div style={{ padding: '0 15px 12px', background: '#fcfbf9' }}>
          {MORE.map((g) => (
            <div key={g.sec} style={{ marginTop: 10 }}>
              <div style={{ fontSize: '0.625rem', fontWeight: 800, color: '#a39c92', letterSpacing: '0.06em', margin: '2px 0 1px' }}>{g.sec}</div>
              {g.rows.map((r) => <MetricLine key={r.label} r={r} col={col} showPrev={showPrev} />)}
            </div>
          ))}
        </div>
      )}

      <button type="button" onClick={() => setOpen((v) => !v)} style={{
        border: 'none', borderTop: `1px solid ${LINE}`, background: '#faf9f7', color: accent,
        padding: '8px 0', cursor: 'pointer', fontSize: '0.72rem', fontWeight: 700,
        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 3, marginTop: 'auto',
      }}>
        {open ? <><MdExpandLess size={15} /> 閉じる</> : <><MdExpandMore size={15} /> 全指標を表示</>}
      </button>
    </div>
  );
};

export const BreakevenCards: React.FC<{ columns: MatrixColumn[]; showPrev: boolean; periodLabel?: string }> = ({ columns, showPrev, periodLabel }) => {
  // セクション化: サマリ(total) / 各飲食グループ(group→配下store) / その他部門(nonstore)
  const sections: { title: string; color: string; featured: boolean; cards: MatrixColumn[] }[] = [];
  const summary = { title: 'サマリ', color: CAT.summary, featured: true, cards: [] as MatrixColumn[] };
  const nonstore = { title: 'その他部門', color: CAT.nonstore, featured: false, cards: [] as MatrixColumn[] };
  let cur: { title: string; color: string; featured: boolean; cards: MatrixColumn[] } | null = null;
  for (const c of columns) {
    if (c.kind === 'total') summary.cards.push(c);
    else if (c.kind === 'group') { cur = { title: c.label, color: CAT[c.key] || CAT.summary, featured: true, cards: [c] }; sections.push(cur); }
    else if (c.kind === 'store') { if (cur) cur.cards.push(c); }
    else if (c.kind === 'nonstore') nonstore.cards.push(c);
  }
  const all = [summary, ...sections, nonstore].filter((s) => s.cards.length);

  return (
    <div>
      {periodLabel && <div style={{ fontSize: '0.78rem', color: SUB, fontWeight: 700, marginBottom: 6 }}>対象期間: {periodLabel}</div>}
      {all.map((s) => (
        <div key={s.title} style={{ marginBottom: 22 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 9, margin: '4px 2px 11px' }}>
            <span style={{ width: 4, height: 17, borderRadius: 2, background: s.color }} />
            <span style={{ fontSize: '0.92rem', fontWeight: 800, color: INK, letterSpacing: '0.02em' }}>{s.title}</span>
            <span style={{ fontSize: '0.7rem', color: FAINT }}>{s.cards.length}部門</span>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(248px, 1fr))', gap: 13, alignItems: 'start' }}>
            {s.cards.map((c) => <DeptCard key={c.key} col={c} accent={s.color} featured={s.featured && (c.kind === 'total' || c.kind === 'group')} showPrev={showPrev} />)}
          </div>
        </div>
      ))}
    </div>
  );
};

export default BreakevenCards;
